import React, { useId } from "react";
import {
  Cable,
  CircleAlert,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";

import { useAccountConnectionScope } from "./AccountConnectionScopeContext";
import { useLanguage } from "../../localization/LanguageContext";
import "./ConnectionScopeGate.css";

const joinClasses = (...values) => values.filter(Boolean).join(" ");

function GateState({
  kind,
  icon: Icon,
  title,
  description,
  action,
  className,
}) {
  const generatedId = useId().replace(/:/g, "");
  const titleId = `connection-scope-gate-${generatedId}-title`;
  const descriptionId = `connection-scope-gate-${generatedId}-description`;
  const isError = kind === "error";
  const isLoading = kind === "loading";

  return (
    <section
      className={joinClasses("connection-scope-gate", className)}
      data-kind={kind}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      aria-busy={isLoading || undefined}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <span className="connection-scope-gate__icon" aria-hidden="true">
        <Icon className={isLoading ? "connection-scope-gate__spinner" : ""} size={24} />
      </span>
      <div className="connection-scope-gate__copy">
        <h2 id={titleId}>{title}</h2>
        <p id={descriptionId}>{description}</p>
      </div>
      {action && <div className="connection-scope-gate__actions">{action}</div>}
    </section>
  );
}

/**
 * Fail-closed boundary for modules that require one explicit connection.
 * Children are not mounted until the shared provider has verified that exact
 * connection against the active account and membership.
 */
export default function ConnectionScopeGate({ children, className = "" }) {
  const scope = useAccountConnectionScope();
  const { copy } = useLanguage();

  if (!scope.required) return children;

  if (scope.status === "error") {
    return (
      <GateState
        kind="error"
        icon={CircleAlert}
        title={copy("Connection access could not be verified")}
        description={copy("Operational data remains closed. Check the service and retry the authorized connection check.")}
        className={className}
        action={(
          <button type="button" onClick={scope.refresh}>
            <RefreshCw size={16} aria-hidden="true" />
            {copy("Retry connection check")}
          </button>
        )}
      />
    );
  }

  if (scope.status !== "ready" || !scope.context) {
    return (
      <GateState
        kind="loading"
        icon={LoaderCircle}
        title={copy("Checking connection access")}
        description={copy("Verifying the account and the connections assigned to your membership.")}
        className={className}
      />
    );
  }

  if (!scope.options.length) {
    return (
      <GateState
        kind="empty"
        icon={Cable}
        title={copy("No connections are available")}
        description={copy("Ask an account owner to configure and assign a connection before using this module.")}
        className={className}
        action={(
          <button type="button" onClick={scope.refresh}>
            <RefreshCw size={16} aria-hidden="true" />
            {copy("Refresh connection access")}
          </button>
        )}
      />
    );
  }

  if (scope.selectionRequired || !scope.selectedConnectionId) {
    return (
      <GateState
        kind="selection"
        icon={ShieldCheck}
        title={copy("Select a connection to continue")}
        description={copy("Choose one server-authorized connection from the module header. Operational data will load only after verification.")}
        className={className}
      />
    );
  }

  return children;
}
